import * as React from 'react';
import styles from './index.less';
import { Input } from 'antd';
import { connect } from 'dva';
import { Dispatch } from 'redux';
import { ConnectState, NodeCreatorPageModelState } from '@/models/connect';
import * as AHooks from 'ahooks';
import { OnMount_Page_Action, OnUnmount_Page_Action } from '@/models/nodeCreatorPage';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FHotspotTooltip, { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import { pinyin } from 'pinyin-pro';
import fMessage from '@/components/fMessage';
import { OnAdd_Node_Action } from '@/models/nodes';
import { history } from '@@/core/history';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';

interface NodeCreatorProps {
  dispatch: Dispatch;
  nodeCreatorPage: NodeCreatorPageModelState;
}

function NodeCreator({ nodeCreatorPage, dispatch }: NodeCreatorProps) {
  const [$title, set$title, get$title] = FUtil.Hook.useGetState<string>('');
  const [$title_Error, set$title_Error, get$title_Error] = FUtil.Hook.useGetState<string>('');

  const [$name, set$name, get$name] = FUtil.Hook.useGetState<string>('');
  const [$name_Optimized, set$name_Optimized, get$name_Optimized] =
    FUtil.Hook.useGetState<string>('');
  const [$name_Error, set$name_Error, get$name_Error] = FUtil.Hook.useGetState<string>('');
  const [$name_Duplicate, set$name_Duplicate, get$name_Duplicate] = FUtil.Hook.useGetState<
    'right' | 'error' | 'checking'
  >('right');

  const [$domain, set$domain, get$domain] = FUtil.Hook.useGetState<string>('');
  const [$domain_Optimized, set$domain_Optimized, get$domain_Optimized] =
    FUtil.Hook.useGetState<string>('');
  const [$domain_Error, set$domain_Error, get$domain_Error] = FUtil.Hook.useGetState<string>('');
  const [$domain_Duplicate, set$domain_Duplicate, get$domain_Duplicate] = FUtil.Hook.useGetState<
    'right' | 'error' | 'checking'
  >('right');

  const [$submitButtonDisabled, set$submitButtonDisabled, get$submitButtonDisabled] =
    FUtil.Hook.useGetState<boolean>(true);

  AHooks.useMount(() => {
    self._czc?.push(['_trackPageview', self.location.pathname]);
    dispatch<OnMount_Page_Action>({
      type: 'nodeCreatorPage/onMount_Page',
    });
  });

  AHooks.useUnmount(() => {
    dispatch<OnUnmount_Page_Action>({
      type: 'nodeCreatorPage/onUnmount_Page',
    });
  });

  const { run: run_nodeName_generate, cancel: cancel_nodeName_generate } = AHooks.useDebounceFn(
    async () => {
      if (get$name_Error() === '') {
        let name_Optimized: string = get$name_Optimized();
        set$name_Duplicate('checking');
        let ran: string = '';
        do {
          const testName: string =
            ran !== '' ? name_Optimized.substring(0, 19) + ran : name_Optimized.substring(0, 24);
          const params2: Parameters<typeof FServiceAPI.Node.details>[0] = {
            nodeName: testName,
          };
          const { data: data2 } = await FServiceAPI.Node.details(params2);
          if (!data2) {
            name_Optimized = testName;
            break;
          }
          ran = (Math.random() * 100000).toFixed();
        } while (true);
        set$name_Optimized(name_Optimized);
        set$name_Duplicate('right');
      }
    },
    {
      wait: 300,
    },
  );

  const { run: run_nodeDomain_generate, cancel: cancel_nodeDomain_generate } = AHooks.useDebounceFn(
    async () => {
      if (get$domain_Error() === '') {
        set$domain_Duplicate('checking');
        let domain_Optimized: string = get$domain_Optimized();
        let ran: string = '';
        do {
          const testDomain: string =
            ran !== ''
              ? domain_Optimized.substring(0, 19) + ran
              : domain_Optimized.substring(0, 24);
          const params1: Parameters<typeof FServiceAPI.Node.details>[0] = {
            nodeDomain: testDomain,
          };
          const { data: data1 } = await FServiceAPI.Node.details(params1);
          if (!data1) {
            domain_Optimized = testDomain;
            break;
          }
          ran = (Math.random() * 100000).toFixed();
        } while (true);
        set$domain_Optimized(domain_Optimized);
        set$domain_Duplicate('right');
      }
    },
    {
      wait: 300,
    },
  );

  AHooks.useDebounceEffect(
    () => {
      set$submitButtonDisabled(
        $title.trim() === '' ||
          $title_Error !== '' ||
          $name_Optimized.trim() === '' ||
          $name_Duplicate !== 'right' ||
          $name_Error !== '' ||
          $domain.trim() === '' ||
          $domain_Duplicate !== 'right' ||
          $domain_Error !== '',
      );
    },
    [
      $title,
      $title_Error,
      $name_Optimized,
      $name_Duplicate,
      $name_Error,
      $domain,
      $domain_Duplicate,
      $domain_Error,
    ],
    {
      wait: 100,
    },
  );

  if (!$submitButtonDisabled) {
    fSetHotspotTooltipVisible('createNodePage.createBtn', {
      value: true,
      effectiveImmediately: true,
      onlyNullish: true,
    });

    setTimeout(() => {
      fSetHotspotTooltipVisible('createNodePage.createBtn', {
        value: false,
        effectiveImmediately: false,
        onlyNullish: false,
      });
    });
  }

  async function onChange_Title(title: string) {
    const oldTitle: string = get$title();
    set$title(title);

    let title_Error: string = '';
    if (title === '') {
      title_Error = FI18n.i18nNext.tAuto('naming_convention_node_title_required');
    } else if (title.length > 100) {
      title_Error = '不超过100个字符';
    }
    set$title_Error(title_Error);
    if (oldTitle === get$name()) {
      onChange_Name(title.substring(0, 24));
    }

    if (oldTitle === get$domain()) {
      onChange_Domain(title.substring(0, 24));
    }
  }

  async function onChange_Name(input: string) {
    cancel_nodeName_generate();
    set$name(input);
    let name_Optimized: string = input
      .replace(new RegExp(/[^\u4E00-\u9FA5|a-zA-Z0-9]/, 'g'), '')
      .substring(0, 24);

    set$name_Optimized(name_Optimized);

    let nodeNameError: string = '';
    if (name_Optimized === '') {
      if (input === '') {
        nodeNameError = FI18n.i18nNext.tAuto('naming_convention_node_id_required');
      } else {
        nodeNameError = FI18n.i18nNext.tAuto('naming_convention_node_name');
      }
    } else if (!FUtil.Regexp.NODE_NAME.test(name_Optimized)) {
      // nodeNameError = '长度必须在 1-100 字符之间。\n' +
      //   '不能以正斜线（/）或者反斜线（\\）开头。\n' +
      //   '开头和结尾的空格会自动删除。';
      nodeNameError = FI18n.i18nNext.tAuto('naming_convention_node_name');
    }
    set$name_Error(nodeNameError);

    if (nodeNameError !== '') {
      return;
    }

    run_nodeName_generate();
  }

  async function onChange_Domain(input: string) {
    cancel_nodeDomain_generate();
    set$domain(input);

    const domain_Optimized: string = pinyin(input, {
      toneType: 'none',
      separator: '',
    })
      .toLowerCase()
      .replace(new RegExp(/[^a-z0-9\-]/, 'g'), '')
      .substring(0, 24);

    set$domain_Optimized(domain_Optimized);
    let nodeDomainError: string = '';
    if (domain_Optimized === '') {
      if (input === '') {
        nodeDomainError = FI18n.i18nNext.tAuto('naming_convention_node_address_required');
      } else {
        nodeDomainError = FI18n.i18nNext.tAuto('naming_convention_node_domain');
      }
    } else if (!FUtil.Regexp.NODE_DOMAIN.test(domain_Optimized)) {
      // nodeDomainError = '只能包括小写字母、数字和短横线（-）。\n' +
      //   '必须以小写字母或者数字开头和结尾。\n' +
      //   '长度必须在 4-24 字符之间。';
      nodeDomainError = FI18n.i18nNext.tAuto('naming_convention_node_domain');
    }
    set$domain_Error(nodeDomainError);

    if (nodeDomainError !== '') {
      return;
    }

    run_nodeDomain_generate();
  }

  return (
    <div className={styles.styles}>
      <div className={styles.content}>
        <div className={styles.header}>
          <FComponentsLib.FTitleText
            type={'h1'}
            text={
              // 创建节点
              FI18n.i18nNext.tAuto('nav_CreateNewNode')
            }
            style={{ fontWeight: 600 }}
          />
        </div>
        <div className={styles.body}>
          <FComponentsLib.FContentText
            type={'highlight'}
            text={
              // 节点标题
              FI18n.i18nNext.tAuto('createnode_input_nodetitle_label')
            }
          />
          <div style={{ height: 10 }} />
          <FInput_PinyinSafeTextCounter
            lengthLimit={100}
            value={$title}
            style={{ width: '100%' }}
            placeholder={
              // 输入节点标题
              FI18n.i18nNext.t('createnode_input_nodetitle_hint')
            }
            onChangeValue={(value) => {
              onChange_Title(value.trim());
            }}
            hasError={$title_Error !== ''}
          />
          {$title_Error !== '' && (
            <>
              <div style={{ height: 5 }} />
              <FComponentsLib.FContentText
                text={$title_Error}
                type={'additional2'}
                style={{ color: '#EE4040' }}
              />
            </>
          )}
          <div style={{ height: 30 }} />
          <FComponentsLib.FContentText
            type={'highlight'}
            text={
              // 节点标识
              FI18n.i18nNext.tAuto('createnode_input_nodeid_label')
            }
          />
          <div style={{ height: 5 }} />
          <FComponentsLib.FContentText
            type={'additional2'}
            text={
              // 节点标识是节点在Freelog授权系统中唯一标识符，一旦创建，不可修改。
              FI18n.i18nNext.tAuto('createnode_input_nodeid_alarm')
            }
          />
          <div style={{ height: 10 }} />
          <FInput_PinyinSafeTextCounter
            lengthLimit={24}
            value={$name}
            style={{ width: '100%' }}
            placeholder={
              // 输入节点标识
              FI18n.i18nNext.t('createnode_input_nodeid_hint')
            }
            onChangeValue={(value) => {
              onChange_Name(value);
            }}
            hasError={$name_Error !== ''}
          />
          {$name_Error !== '' && (
            <>
              <div style={{ height: 5 }} />
              <pre className={styles.errorTip}>{$name_Error}</pre>
            </>
          )}

          {$name_Error === '' && $name_Duplicate === 'error' && (
            <>
              <div style={{ height: 5 }} />
              <FComponentsLib.FContentText
                text={'该节点标识已经存在或已经被其它用户使用'}
                type={'additional2'}
                style={{ color: '#EE4040' }}
              />
            </>
          )}

          {$name_Error === '' && $name_Duplicate !== 'error' && $name !== $name_Optimized && (
            <>
              <div style={{ height: 5 }} />
              <div>
                <div
                  style={{
                    color: '#44C28C',
                    fontSize: 12,
                    lineHeight: '18px',
                  }}
                >
                  {/*你的节点标识将自动转换为{nodeid}*/}
                  {/*你的节点标识将自动转换为 <strong>{$name_Optimized}</strong>*/}
                  {FI18n.i18nNext.tAuto('createnode_input_nodeid_automodified_msg', {
                    nodeid: $name_Optimized,
                  })}
                </div>
                <div style={{ height: 5 }} />
                <div
                  style={{
                    color: '#E9A923',
                    fontSize: 12,
                    lineHeight: '18px',
                  }}
                >
                  {/*节点标识只能包括中文、英文和数字，长度必须在 2-24 字符之间。且不能与现有标识重复*/}
                  {FI18n.i18nNext.tAuto('naming_convention_node_name')}
                </div>
              </div>
            </>
          )}

          <div style={{ height: 30 }} />
          <FComponentsLib.FContentText
            type={'highlight'}
            // text={'节点地址'}
            text={FI18n.i18nNext.tAuto('createnode_input_nodeaddress_label')}
          />
          <div style={{ height: 5 }} />
          <FComponentsLib.FContentText
            type={'additional2'}
            // text={'节点地址必须全局唯一，输入的地址不能和已存在的节点地址重复。'}
            text={FI18n.i18nNext.tAuto('naming_convention_node_domain')}
          />
          <div style={{ height: 10 }} />
          {/* <Input */}
          <FInput_PinyinSafeTextCounter
            lengthLimit={-1}
            value={$domain}
            style={{ width: '100%' }}
            // style={{ height: 38 }}
            suffix={
              <FComponentsLib.FContentText
                text={FUtil.Domain.getEnvSuffix()}
                type={'additional2'}
              />
            }
            placeholder={
              // 输入节点地址
              FI18n.i18nNext.t('createnode_input_nodeaddress_hint')
            }
            onChangeValue={(value) => {
              const domain: string = value.trim();
              onChange_Domain(domain);
            }}
            hasError={$domain_Error !== ''}
          />

          {$domain_Error !== '' && (
            <>
              <div style={{ height: 5 }} />
              <pre className={styles.errorTip}>{$domain_Error}</pre>
            </>
          )}
          {$domain_Error === '' && $domain_Duplicate === 'error' && (
            <>
              <div style={{ height: 5 }} />
              <FComponentsLib.FContentText
                text={'该节点地址已经存在或已经被其它用户使用'}
                type={'additional2'}
                style={{ color: '#EE4040' }}
              />
            </>
          )}

          {$domain_Error === '' &&
            $domain_Duplicate !== 'error' &&
            $domain !== $domain_Optimized && (
              <>
                <div style={{ height: 5 }} />
                <div>
                  <div
                    style={{
                      color: '#44C28C',
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    你的域名将自动转换为 <strong>{$domain_Optimized}</strong>
                  </div>
                  <div style={{ height: 5 }} />
                  <div
                    style={{
                      color: '#E9A923',
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    {/*节点标识只能包括中文、英文和数字，长度必须在 2-24 字符之间。且不能与现有标识重复*/}
                    {FI18n.i18nNext.tAuto('naming_convention_node_domain')}
                  </div>
                </div>
              </>
            )}

          <div style={{ height: 40 }} />
          <div className={styles.bottom}>
            <FHotspotTooltip
              id={'createNodePage.createBtn'}
              style={{ left: -52, top: 4 }}
              text={FI18n.i18nNext.tAuto('hotpots_createnode_btn_create')}
            >
              <FComponentsLib.FRectBtn
                disabled={$submitButtonDisabled}
                size={'large'}
                type={'primary'}
                style={{ paddingLeft: 50, paddingRight: 50 }}
                onClick={async () => {
                  const params: Parameters<typeof FServiceAPI.Node.create>[0] = {
                    nodeDomain: get$domain_Optimized().trim(),
                    nodeName: get$name_Optimized().trim(),
                    nodeTitle: get$title().trim(),
                  };

                  const {
                    data,
                    ret,
                    errCode,
                    msg,
                  }: {
                    data: {
                      nodeDomain: string;
                      nodeId: number;
                      nodeName: string;
                      nodeTitle: string;
                    };
                    ret: number;
                    errCode: number;
                    msg: string;
                  } = await FServiceAPI.Node.create(params);

                  if (ret !== 0 || errCode !== 0 || !data) {
                    self._czc?.push(['_trackEvent', '创建节点页', '创建节点', '', 0]);
                    fMessage(msg, 'error');
                    return;
                  }

                  dispatch<OnAdd_Node_Action>({
                    type: 'nodes/onAdd_Node',
                    payload: {
                      value: {
                        nodeDomain: data.nodeDomain,
                        nodeId: data.nodeId,
                        nodeName: data.nodeName,
                        nodeTitle: data.nodeTitle,
                      },
                    },
                  });

                  self._czc?.push(['_trackEvent', '创建节点页', '创建节点', '', 1]);
                  history.push(FUtil.LinkTo.nodeCreateSuccess({ nodeID: data.nodeId }));
                }}
              >
                {/*创建节点*/}
                {FI18n.i18nNext.tAuto('btn_create_node')}
              </FComponentsLib.FRectBtn>
            </FHotspotTooltip>
            <div style={{ height: 20 }} />
            <FComponentsLib.FContentText
              style={{ color: '#BEBEBE' }}
              type={'additional2'}
              // text={'每个用户最多可创建3个节点，节点创建之后无法删除，请谨慎操作。'}
              text={FI18n.i18nNext.tAuto('info_node_maximum')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default connect(({ nodeCreatorPage }: ConnectState) => ({
  nodeCreatorPage,
}))(NodeCreator);
